// lib/documentExtractor.ts
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export interface ExtractedDocument {
  text: string;
  type: 'pdf' | 'docx' | 'txt';
  fileName: string;
  pageCount?: number;
  wordCount: number;
}

/**
 * Extract text from PDF file
 */
export async function extractTextFromPDF(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  
  let fullText = '';
  
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items
      .map((item: any) => item.str)
      .join(' ');
    fullText += pageText + '\n\n';
  }
  
  return fullText.trim();
}

/**
 * Extract text from Word document
 */
export async function extractTextFromDocx(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value.trim();
}

/**
 * Extract text from plain text file
 */
export async function extractTextFromTxt(file: File): Promise<string> {
  const text = await file.text();
  return text.trim();
}

/**
 * Main extraction function - detects file type and extracts text
 */
export async function extractDocumentText(file: File): Promise<ExtractedDocument> {
  let text = '';
  let type: 'pdf' | 'docx' | 'txt' = 'txt';
  let pageCount: number | undefined;
  
  if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
    type = 'pdf';
    text = await extractTextFromPDF(file);
    
    // Get page count
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    pageCount = pdf.numPages;
  } else if (
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    file.name.toLowerCase().endsWith('.docx')
  ) {
    type = 'docx';
    text = await extractTextFromDocx(file);
  } else if (file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt')) {
    type = 'txt';
    text = await extractTextFromTxt(file);
  } else {
    throw new Error(`Unsupported file type: ${file.type}`);
  }
  
  const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
  
  return {
    text,
    type,
    fileName: file.name,
    pageCount,
    wordCount,
  };
}
